import { z } from 'zod';
import { parseRequestBody, tryToResolveJsonBody } from './utils/bodyUtils.js';
import { isFailure } from './utils/either.js';
import { buildWretchError } from './utils/errorUtils.js';
import { parseQueryParams } from './utils/queryUtils.js';
export const UNKNOWN_SCHEMA = z.unknown();
function resolveHeaders(headers) {
    return (typeof headers === 'function' ? headers() : headers) ?? {};
}
function handleBodyParseError(bodyParseResult, params, response) {
    if (bodyParseResult.error === 'NOT_JSON') {
        if (!params.isNonJSONResponseExpected) {
            return Promise.reject(buildWretchError(`Request to ${params.path} has returned an unexpected non-JSON response.`, response));
        }
        return response;
    }
    if (bodyParseResult.error === 'EMPTY_RESPONSE') {
        if (!params.isEmptyResponseExpected) {
            return Promise.reject(buildWretchError(`Request to ${params.path} has returned an unexpected empty response.`, response));
        }
        return null;
    }
    return Promise.reject(bodyParseResult.error);
}
async function sendResourceChange(wretch, method, params) {
    const body = parseRequestBody({
        body: params.body,
        requestBodySchema: params.requestBodySchema,
        path: params.path,
    });
    if (isFailure(body)) {
        return Promise.reject(body.error);
    }
    const queryParams = parseQueryParams({
        queryParams: params.queryParams,
        queryParamsSchema: params.queryParamsSchema,
        path: params.path,
    });
    if (isFailure(queryParams)) {
        return Promise.reject(queryParams.error);
    }
    const resolvedHeaders = await resolveHeaders(params.headers);
    return wretch
        .headers(resolvedHeaders)[method](body.result, `${params.path}${queryParams.result}`)
        .res(async (response) => {
        const bodyParseResult = await tryToResolveJsonBody(response, params.path, params.responseBodySchema, params.isEmptyResponseExpected);
        if (bodyParseResult.error) {
            return handleBodyParseError(bodyParseResult, params, response);
        }
        return bodyParseResult.result;
    });
}
/* GET */
export async function sendGet(wretch, params) {
    const queryParams = parseQueryParams({
        queryParams: params.queryParams,
        queryParamsSchema: params.queryParamsSchema,
        path: params.path,
    });
    if (isFailure(queryParams)) {
        return Promise.reject(queryParams.error);
    }
    const resolvedHeaders = await resolveHeaders(params.headers);
    return wretch
        .headers(resolvedHeaders)
        .get(`${params.path}${queryParams.result}`)
        .res(async (response) => {
        const bodyParseResult = await tryToResolveJsonBody(response, params.path, params.responseBodySchema, params.isEmptyResponseExpected);
        if (bodyParseResult.error) {
            return handleBodyParseError(bodyParseResult, params, response);
        }
        return bodyParseResult.result;
    });
}
/* POST */
export function sendPost(wretch, params) {
    return sendResourceChange(wretch, 'post', params);
}
/* PUT */
export function sendPut(wretch, params) {
    return sendResourceChange(wretch, 'put', params);
}
/* PATCH */
export function sendPatch(wretch, params) {
    return sendResourceChange(wretch, 'patch', params);
}
/* DELETE */
export async function sendDelete(wretch, params) {
    const queryParams = parseQueryParams({
        queryParams: params.queryParams,
        queryParamsSchema: params.queryParamsSchema,
        path: params.path,
    });
    if (isFailure(queryParams)) {
        return Promise.reject(queryParams.error);
    }
    const resolvedHeaders = await resolveHeaders(params.headers);
    return wretch
        .headers(resolvedHeaders)
        .delete(`${params.path}${queryParams.result}`)
        .res(async (response) => {
        const bodyParseResult = await tryToResolveJsonBody(response, params.path, params.responseBodySchema ?? UNKNOWN_SCHEMA, params.isEmptyResponseExpected ?? true);
        if (bodyParseResult.error) {
            return handleBodyParseError(bodyParseResult, {
                isNonJSONResponseExpected: params.isNonJSONResponseExpected,
                path: params.path,
                isEmptyResponseExpected: params.isEmptyResponseExpected ?? true,
            }, response);
        }
        return bodyParseResult.result;
    });
}
export function sendByPayloadRoute(wretch, routeDefinition, params) {
    return sendResourceChange(wretch, routeDefinition.method, {
        // @ts-expect-error magic type inferring happening
        body: params.body,
        isEmptyResponseExpected: routeDefinition.isEmptyResponseExpected,
        isNonJSONResponseExpected: routeDefinition.isNonJSONResponseExpected,
        // biome-ignore lint/suspicious/noExplicitAny: FixMe try to find a solution
        requestBodySchema: routeDefinition.requestBodySchema,
        // biome-ignore lint/suspicious/noExplicitAny: FixMe try to find a solution
        responseBodySchema: routeDefinition.successResponseBodySchema,
        // @ts-expect-error magic type inferring happening
        queryParams: params.queryParams,
        queryParamsSchema: routeDefinition.requestQuerySchema,
        // @ts-expect-error magic type inferring happening
        path: routeDefinition.pathResolver(params.pathParams),
        // @ts-expect-error FixMe
        headers: params.headers,
        // @ts-expect-error magic type inferring happening
        headersSchema: params.headersSchema,
    });
}
export function sendByGetRoute(wretch, routeDefinition, params) {
    return sendGet(wretch, {
        isEmptyResponseExpected: routeDefinition.isEmptyResponseExpected,
        isNonJSONResponseExpected: routeDefinition.isNonJSONResponseExpected,
        responseBodySchema: routeDefinition.successResponseBodySchema,
        // @ts-expect-error magic type inferring happening
        queryParams: params.queryParams,
        queryParamsSchema: routeDefinition.requestQuerySchema,
        // @ts-expect-error magic type inferring happening
        path: routeDefinition.pathResolver(params.pathParams),
        // @ts-expect-error FixMe
        headers: params.headers,
        // @ts-expect-error magic type inferring happening
        headersSchema: params.headersSchema,
    });
}
export function sendByDeleteRoute(wretch, routeDefinition, params) {
    return sendDelete(wretch, {
        isEmptyResponseExpected: routeDefinition.isEmptyResponseExpected,
        isNonJSONResponseExpected: routeDefinition.isNonJSONResponseExpected,
        responseBodySchema: routeDefinition.successResponseBodySchema,
        // @ts-expect-error magic type inferring happening
        queryParams: params.queryParams,
        queryParamsSchema: routeDefinition.requestQuerySchema,
        // @ts-expect-error magic type inferring happening
        path: routeDefinition.pathResolver(params.pathParams),
        // @ts-expect-error FIXME
        headers: params.headers,
        // @ts-expect-error FIXME
        headersSchema: params.headersSchema,
    });
}
//# sourceMappingURL=client.js.map